import type { MouseEvent } from 'react';
import { Box, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { Link } from 'react-router-dom';
import type { UnifiedAssetResult } from '../api/unifiedSearchTypes';
import type { EventFromAsset } from '../api/types';
import { phoneSurface } from '../../layout/chrome';
import { AdvancedSearchIcon } from '../../layout/mobile/icons';
import { formatVolume } from './searchFormat';

// The asset kinds the search answers with, as the short label the mockup shows.
const ASSET_TYPE_LABELS: Record<string, string> = {
    cryptocurrency: 'Crypto',
    etf: 'ETF',
};

export const assetTypeLabel = (assetType: string) =>
    ASSET_TYPE_LABELS[assetType.toLowerCase()] ??
    assetType.charAt(0).toUpperCase() + assetType.slice(1);

const ROW = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    minHeight: 52,
    px: 2,
    cursor: 'pointer',
} as const;

interface PhoneSearchResultsProps {
    assets: UnifiedAssetResult[];
    events: EventFromAsset[];
    query: string;
    /** The row the arrow keys are on (a hardware keyboard on a tablet). */
    selectedIdx: number;
    /** Where Advanced search is; no link row without it. */
    advancedHref?: string;
    /** Pixels, or a CSS length: the panel is as tall as its rows, up to this. */
    maxHeight: number | string;
    onSelectAsset: (asset: UnifiedAssetResult) => void;
    onSelectEvent: (event: EventFromAsset) => void;
    /** A tap on the panel's empty space: iOS does not blur a field for that. */
    onDismiss: () => void;
}

// The results while typing on a phone: a full-bleed white panel between the
// top bar and the field row (the field's dock is its containing block), the
// way the approved mockup draws it. Assets first, then markets, the Advanced
// search row pinned under them. The desktop dropdown is a different thing and
// stays in SearchBar.
export function PhoneSearchResults({
    assets,
    events,
    query,
    selectedIdx,
    advancedHref,
    maxHeight,
    onSelectAsset,
    onSelectEvent,
    onDismiss,
}: PhoneSearchResultsProps) {
    const theme = useTheme();
    const hairline = alpha(theme.palette.text.primary, 0.08);
    const trimmed = query.trim();
    const advancedTo =
        advancedHref && trimmed
            ? `${advancedHref}?q=${encodeURIComponent(trimmed)}`
            : advancedHref;

    const header = (label: string, divided: boolean) => (
        <Typography
            sx={{
                px: 2,
                pt: '14px',
                pb: '6px',
                fontSize: 12,
                fontWeight: 500,
                letterSpacing: '0.06em',
                color: 'text.secondary',
                ...(divided ? { borderTop: 1, borderColor: hairline } : {}),
            }}
        >
            {label}
        </Typography>
    );

    return (
        <Box
            data-testid="search-results"
            data-placement="above"
            // The field's dock is the positioned ancestor: left and right 0
            // are the screen's edges, bottom 100% the top of the field row.
            onClick={(e: MouseEvent<HTMLElement>) => {
                if (
                    !(e.target as Element).closest('[data-result-row],a,button')
                )
                    onDismiss();
            }}
            onKeyDown={e => {
                if (e.key === 'Escape') onDismiss();
            }}
            sx={{
                position: 'absolute',
                left: 0,
                right: 0,
                bottom: '100%',
                maxHeight,
                display: 'flex',
                flexDirection: 'column',
                bgcolor: phoneSurface(theme),
            }}
        >
            <Box
                sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: 'auto',
                    overscrollBehavior: 'contain',
                }}
            >
                {assets.length > 0 && header('ASSETS', false)}
                {assets.map((a, i) => (
                    <Box
                        key={`asset-${a.ticker}`}
                        data-result-row
                        onMouseDown={e => {
                            e.preventDefault();
                            onSelectAsset(a);
                        }}
                        sx={{
                            ...ROW,
                            bgcolor:
                                selectedIdx === i
                                    ? 'action.hover'
                                    : 'transparent',
                            '&:hover': { bgcolor: 'action.hover' },
                        }}
                    >
                        <Box
                            component="span"
                            sx={{
                                minWidth: 48,
                                flexShrink: 0,
                                fontSize: 15,
                                fontWeight: 700,
                            }}
                        >
                            {a.ticker}
                        </Box>
                        <Box
                            component="span"
                            sx={{
                                flex: 1,
                                minWidth: 0,
                                fontSize: 15,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {a.asset_name}
                        </Box>
                        <Box
                            component="span"
                            sx={{
                                flexShrink: 0,
                                fontSize: 12,
                                px: 1,
                                py: '3px',
                                borderRadius: '10px',
                                bgcolor: alpha(
                                    theme.palette.text.primary,
                                    0.08
                                ),
                                color: alpha(theme.palette.text.primary, 0.7),
                            }}
                        >
                            {assetTypeLabel(a.asset_type)}
                        </Box>
                    </Box>
                ))}

                {events.length > 0 && header('MARKETS', assets.length > 0)}
                {events.map((ev, i) => {
                    const globalIdx = assets.length + i;
                    return (
                        <Box
                            key={`event-${ev.event_id}`}
                            data-result-row
                            onMouseDown={e => {
                                e.preventDefault();
                                onSelectEvent(ev);
                            }}
                            sx={{
                                ...ROW,
                                bgcolor:
                                    selectedIdx === globalIdx
                                        ? 'action.hover'
                                        : 'transparent',
                                '&:hover': { bgcolor: 'action.hover' },
                            }}
                        >
                            {ev.image && (
                                <Box
                                    component="img"
                                    src={ev.image}
                                    alt=""
                                    sx={{
                                        width: 28,
                                        height: 28,
                                        borderRadius: '4px',
                                        objectFit: 'cover',
                                        flexShrink: 0,
                                    }}
                                />
                            )}
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Box
                                    component="span"
                                    sx={{
                                        display: 'block',
                                        fontSize: 15,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {ev.event_title}
                                </Box>
                                {ev.market_count != null && (
                                    <Box
                                        component="span"
                                        sx={{
                                            display: 'block',
                                            fontSize: 12,
                                            color: 'text.secondary',
                                        }}
                                    >
                                        {ev.market_count} market
                                        {ev.market_count !== 1 ? 's' : ''}
                                        {ev.volume != null &&
                                            ev.volume > 0 &&
                                            ` · ${formatVolume(ev.volume)}`}
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    );
                })}
            </Box>

            {advancedTo && (
                <Box
                    component={Link}
                    to={advancedTo}
                    // As the advanced-search button this replaces: the press
                    // must not take focus from the field, or the blur ends the
                    // search before the tap lands.
                    onMouseDown={(e: MouseEvent) => e.preventDefault()}
                    sx={{
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        minHeight: 48,
                        px: 2,
                        borderTop: 1,
                        borderColor: hairline,
                        fontSize: 14,
                        fontWeight: 500,
                        color: 'primary.main',
                        textDecoration: 'none',
                    }}
                >
                    <AdvancedSearchIcon sx={{ fontSize: 20 }} />
                    <Box
                        component="span"
                        sx={{
                            minWidth: 0,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {trimmed
                            ? `Advanced search for "${trimmed}"`
                            : 'Advanced search'}
                    </Box>
                </Box>
            )}
        </Box>
    );
}
